import Sidebar from '@apps-common/ui-components/Sidebar/Sidebar.jsx';
import SidebarHeader from '@apps-common/ui-components/Sidebar/SidebarHeader.jsx';
import SidebarItem from '@apps-common/ui-components/Sidebar/SidebarItem.jsx';
import MosaicsIcon from '@apps-common/ui-icons/Mosaics.jsx';
import SavedSearchIcon from '@apps-common/ui-icons/SavedSearch.jsx';
import OverviewIcon from '@mui/icons-material/AutoAwesomeMosaic';
import DestinationsIcon from '@mui/icons-material/Hub';
import BrowserIcon from '@mui/icons-material/Language';
import AreasOfInterestIcon from '@mui/icons-material/LocationOnOutlined';
import SubscriptionIcon from '@mui/icons-material/MediationOutlined';
import DataCollectionsIcon from '@mui/icons-material/StorageOutlined';
import OrdersIcon from '@mui/icons-material/TextSnippetOutlined';
import SearchAndOrderIcon from '@mui/icons-material/TravelExploreOutlined';
import {Link, useLocation} from 'react-router';
import {EVENT_ACTIONS, trackEvent} from '@src/analytics.js';
import {
  DATA_COLLECTIONS_URL,
  EXPLORER_URL,
  FEATURES_MANAGER_URL,
} from '@src/config.ts';
import {
  ANALYZE_RESTORE_PARAM,
  ANALYZE_ROOT_ROUTE,
  ANALYZE_ROUTE_KEYS,
} from '@src/constants/routes';
import {useFlags} from '@src/hooks/useFlags';
import useMapLocationParams from '@src/hooks/useMapLocationParams';

const LocalSidebar = props => {
  const location = useLocation();
  const flags = useFlags();
  const {serialize, zoom, center} = useMapLocationParams();

  const queryParams = serialize({zoom, center});

  const navigationItems = [
    {
      label: 'Overview',
      icon: OverviewIcon,
      to: '/overview',
      component: Link,
    },
    {
      type: 'header',
      label: 'Add data',
    },
    {
      label: 'Search and order',
      icon: SearchAndOrderIcon,
      href: EXPLORER_URL,
    },
    {
      label: 'Subscribe to data',
      icon: SubscriptionIcon,
      to: {
        pathname: '/subscriptions/new',
        search: queryParams,
      },
      component: Link,
      flags: ['showCreateSubscription'],
    },
    {
      label: 'Areas of interest',
      icon: AreasOfInterestIcon,
      href: FEATURES_MANAGER_URL,
    },
    {
      type: 'header',
      label: 'View data delivery',
    },
    {
      label: 'Orders',
      icon: OrdersIcon,
      to: '/orders',
      component: Link,
    },
    {
      label: 'Subscriptions',
      icon: SubscriptionIcon,
      to: '/subscriptions',
      component: Link,
      selectedFn: location =>
        location.pathname.includes('/subscriptions') &&
        location.pathname !== '/subscriptions/new',
    },
    {
      label: 'Data collections',
      icon: DataCollectionsIcon,
      href: DATA_COLLECTIONS_URL,
    },
    {
      label: 'Basemaps',
      icon: MosaicsIcon,
      to: {
        pathname: '/mosaics',
        search: queryParams,
      },
      component: Link,
    },
    {
      label: 'Destinations',
      icon: DestinationsIcon,
      to: '/destinations',
      component: Link,
    },
    {
      type: 'header',
      label: 'Experiments (internal only)',
      flags: ['showMapRoutes', 'showBrowser'],
    },
    {
      label: 'Explorer search (dev)',
      icon: SearchAndOrderIcon,
      to: {
        pathname: '/explorer',
        search: queryParams,
      },
      component: Link,
      flags: ['showMapRoutes'],
    },
    {
      label: 'Saved searches (dev)',
      icon: SavedSearchIcon,
      to: {
        pathname: '/saved-searches',
        search: queryParams,
      },
      component: Link,
      flags: ['showMapRoutes'],
    },
    {
      label: 'AOIs (dev)',
      icon: AreasOfInterestIcon,
      to: {
        pathname: '/aois',
        search: queryParams,
      },
      component: Link,
      flags: ['showMapRoutes'],
    },
    {
      label: 'Visual analysis (dev)',
      icon: BrowserIcon,
      to: {
        pathname: `/${ANALYZE_ROOT_ROUTE}/${ANALYZE_ROUTE_KEYS.BROWSE}`,
        search: `${queryParams}${queryParams.startsWith('?') ? '&' : ''}${ANALYZE_RESTORE_PARAM}`,
      },
      component: Link,
      flags: ['showBrowser'],
      selectedFn: location =>
        location.pathname.startsWith(`/${ANALYZE_ROOT_ROUTE}`),
    },
  ];

  return (
    <Sidebar {...props}>
      {navigationItems.map(item => {
        const hasFlags = !!item.flags && item.flags.length > 0;
        const isFlagged = (item.flags ?? []).some(flag => flags[flag]);

        // If the item has flags, hide the item if the user has none of them set.
        // Also hide the item if hideIfFlag is set and the user has that flag.
        if (
          (hasFlags && !isFlagged) ||
          (!!item.hideIfFlag && flags[item.hideIfFlag])
        ) {
          return null;
        }

        if (item.type === 'header') {
          return <SidebarHeader key={item.label} label={item.label} />;
        }

        const itemIsActive = item.selectedFn
          ? item.selectedFn(location)
          : !!item.to &&
            location.pathname.includes(item.to.pathname || item.to);

        return (
          <SidebarItem
            component={item.component}
            key={item.label}
            label={item.label}
            Icon={item.icon}
            href={item.href}
            to={item.to}
            active={itemIsActive}
            draggable={false}
            onClick={() => {
              trackEvent('App - Navigate to Sidebar Item', {
                appSection: 'App',
                action: EVENT_ACTIONS.NAVIGATE_TO,
                target: 'Sidebar Item',
                to: item.label,
              });
            }}
          />
        );
      })}
    </Sidebar>
  );
};

export default LocalSidebar;
